// Google Analytics 4 (GA4) wrapper.
//
// Performance-safe guarantees:
//   - Script is loaded async and only injected once, after first init call.
//   - Nothing loads if REACT_APP_GA_MEASUREMENT_ID is unset or empty.
//   - Nothing loads on /admin or /crew paths (staff shouldn't pollute data).
//   - All calls go through window.gtag?.() — safe no-op if ad-blocked.

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;

function isStaffPath(pathname: string): boolean {
  return pathname.startsWith('/admin') || pathname.startsWith('/crew');
}

function isAnalyticsEnabled(): boolean {
  if (!MEASUREMENT_ID) return false;
  if (typeof window === 'undefined') return false;
  if (isStaffPath(window.location.pathname)) return false;
  return true;
}

export function initAnalytics(): void {
  if (initialized) return;
  if (!isAnalyticsEnabled()) return;

  initialized = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // send_page_view: false — the app sends its own on every route change,
  // so we don't double-count the initial load.
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

export function trackPageView(pathname: string, title?: string): void {
  if (!isAnalyticsEnabled()) return;
  if (isStaffPath(pathname)) return;
  window.gtag?.('event', 'page_view', {
    page_path: pathname,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
}

export function trackEvent(
  name: string,
  params?: Record<string, unknown>,
): void {
  if (!isAnalyticsEnabled()) return;
  window.gtag?.('event', name, params);
}
